import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProfilkepService } from './profilkep.service';

/**
 *  `<app-profilkep tipus="tanulo" [azon]="n.oktazon" [nev]="n.nev">` – kerek kis kép a névsor
 *  sorában. Amíg (vagy ha) nincs Microsoft-fotó, a név monogramja látszik – külső avatar-
 *  szolgáltatás nélkül. A képet csak akkor kéri, amikor a sor a képernyőre ér.
 */
@Component({
  selector: 'app-profilkep',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<span class="pk" [style.width.px]="meret" [style.height.px]="meret"
      [style.font-size.px]="meret * 0.4" [style.background]="kep ? 'transparent' : szin"
      [attr.title]="kep ? null : (nev || null)"
      (mouseenter)="nagyitas()" (mouseleave)="nagyitasVege()">
      <img *ngIf="kep; else mono" [src]="kep" alt="" loading="lazy" />
      <ng-template #mono>{{ monogram }}</ng-template>
    </span>`,
  styles: [`
    :host { display: inline-flex; vertical-align: middle; flex: 0 0 auto; }
    .pk { display: inline-flex; align-items: center; justify-content: center; border-radius: 50%;
          overflow: hidden; color: #fff; font-weight: 600; line-height: 1; user-select: none; }
    .pk img { width: 100%; height: 100%; object-fit: cover; display: block; }
  `],
})
export class ProfilkepComponent implements OnChanges, OnDestroy {
  @Input() tipus: 'tanulo' | 'dolgozo' | 'sajat' = 'tanulo';
  @Input() azon: string | number | null | undefined;
  @Input() nev = '';
  @Input() meret = 28;

  kep: string | null = null;
  monogram = '';
  szin = '#9aa5b1';
  private figyelo?: IntersectionObserver;
  private kert = '';

  constructor(private szolg: ProfilkepService, private el: ElementRef<HTMLElement>, private cd: ChangeDetectorRef) {}

  ngOnChanges(): void {
    const szavak = String(this.nev || '').replace(/\b(dr|ifj|id)\.?\s+/gi, '').trim().split(/\s+/).filter(Boolean);
    this.monogram = szavak.length ? (szavak[0][0] + (szavak.length > 1 ? szavak[1][0] : '')).toUpperCase() : '?';
    let h = 0;
    for (const c of String(this.nev || this.azon || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    this.szin = `hsl(${h % 360}, 45%, 52%)`;

    const kulcs = `${this.tipus}|${this.azon ?? ''}`;
    if (kulcs === this.kert) return;
    this.kert = kulcs;
    this.kep = null;
    this.figyelo?.disconnect();
    if (typeof IntersectionObserver === 'undefined') { this.betolt(kulcs); return; }
    this.figyelo = new IntersectionObserver((e) => {
      if (e.some((x) => x.isIntersecting)) { this.figyelo?.disconnect(); this.betolt(kulcs); }
    }, { rootMargin: '200px' });
    this.figyelo.observe(this.el.nativeElement);
  }

  private async betolt(kulcs: string): Promise<void> {
    const url = await this.szolg.kep(this.tipus, this.azon);
    if (kulcs !== this.kert) return;           // közben másik személy került a helyére
    this.kep = url;
    this.cd.markForCheck();
  }

  /* ══════════════════════════════════════════════════════════════════════
   *  RÁHÚZÁSRA NAGYÍTÁS (2026-10-21, a user kérése)
   *
   *  A névsorkép 22–36 px: arcot felismerni ekkora méretben nem lehet. Egérrel ráállva a kép
   *  nagyban (a névvel) megjelenik mellette. A nagyított kép a `<body>`-ba kerül, rögzített
   *  helyzetben: egy `transform: scale()` a táblázatcellák és a görgethető listák szélén
   *  levágódna. Csak valódi fotónál – a monogramot nincs mit nagyítani –, és csak egérnél:
   *  érintőképernyőn a „ráhúzás” egy koppintás lenne, ami a sort nyitná meg.
   * ═══════════════════════════════════════════════════════════════════ */
  private elonezet: HTMLElement | null = null;
  private static readonly NAGY = 200;

  nagyitas(): void {
    if (!this.kep || this.elonezet) return;
    if (typeof window === 'undefined' || !window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;
    const meret = ProfilkepComponent.NAGY;
    const doboz = document.createElement('div');
    doboz.className = 'profilkep-nagyitas';
    const kep = document.createElement('img');
    kep.src = this.kep;
    kep.alt = '';
    doboz.appendChild(kep);
    if (this.nev) {
      const nev = document.createElement('div');
      nev.className = 'profilkep-nagyitas-nev';
      nev.textContent = this.nev;
      doboz.appendChild(nev);
    }
    //  Hely: a kép jobb oldalán; ha ott nem fér el, a bal oldalán. Függőlegesen a képernyőn belül.
    const r = this.el.nativeElement.getBoundingClientRect();
    const res = 8;
    const magas = meret + (this.nev ? 34 : 0);
    let bal = r.right + res;
    if (bal + meret > window.innerWidth - res) bal = Math.max(res, r.left - res - meret);
    let fent = r.top + r.height / 2 - magas / 2;
    fent = Math.min(Math.max(res, fent), window.innerHeight - magas - res);
    doboz.style.left = `${Math.round(bal)}px`;
    doboz.style.top = `${Math.round(fent)}px`;
    doboz.style.width = `${meret}px`;
    document.body.appendChild(doboz);
    this.elonezet = doboz;
    //  Görgetéskor a kép a helyén maradna, a sor viszont elmegy alóla – ilyenkor eltüntetjük.
    window.addEventListener('scroll', this.gorgetes, { capture: true, passive: true, once: true });
  }

  private gorgetes = () => this.nagyitasVege();

  nagyitasVege(): void {
    window.removeEventListener('scroll', this.gorgetes, { capture: true } as any);
    this.elonezet?.remove();
    this.elonezet = null;
  }

  ngOnDestroy(): void { this.figyelo?.disconnect(); this.nagyitasVege(); }
}
